import {
  type ReactNode,
  useCallback,
  useId,
  useRef,
  useState,
  createContext,
  useContext,
} from 'react'
import cx from 'classnames'
import {
  ScreenHeaderProvider,
  ScreenHeaderBorder,
  ScreenHeaderContent,
  ScreenHeaderMain,
  ScreenHeaderPersistentTopBar,
  ScreenHeaderPersistentTopBarLayout,
  ScreenHeaderPersistentTopBarTitle,
  ScreenHeaderPersistentTopBarAction,
  ScreenHeaderTitle,
} from '@/components/ScreenHeader'
import { IconButton } from '@/components/IconButton'
import { Icon } from '@/components/Icon'
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateIconDecoration,
  EmptyStateTitle,
} from '@/components/EmptyState'
import { Button } from '@/components/Button'
import {
  AutoSkeleton,
  IsSkeletonContext,
  Skeleton,
} from '@/components/Skeleton'
import { GridList, GridListItem, Provider } from 'react-aria-components'
import {
  type MinidownClassNames,
  MinidownContent,
} from '@/components/MinidownContent'
import { type MinidownNode } from '@/minidown/nodes'
import { dateFormatters } from '@/utils/date-util'
import { useSkeletonListContext } from '@/components/Skeleton/SkeletonList'
import { composeRefs } from '@radix-ui/react-compose-refs'
import { useMotionValueEvent, useScroll } from 'motion/react'
import { Avatar } from '@/components/Avatar'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import { links } from '@/utils/link'
import styles from './ActivityView.module.css'

interface ActivityViewContext {
  isPastActivityScrolled: boolean
  setPastActivityScrolled: (scrolled: boolean) => void
  onMarkAsReadPress?: () => void
  isMarkAsReadPending?: boolean
}

const ActivityViewContext = createContext<ActivityViewContext>({
  isPastActivityScrolled: false,
  setPastActivityScrolled: () => {
    throw new Error('ActivityViewContext not initialized')
  },
})

interface ActivityViewProps {
  isEmpty: boolean
  hasRecentActivity: boolean
  onMarkAsReadPress?: () => void
  isMarkAsReadPending?: boolean
  children: ReactNode
  testId?: string
}

export function ActivityView({
  isEmpty,
  hasRecentActivity,
  onMarkAsReadPress,
  isMarkAsReadPending,
  children,
  testId,
}: ActivityViewProps) {
  const isSpacious = useIsSpacious()
  const [isPastActivityScrolled, setPastActivityScrolled] = useState(false)

  const topBarTitle: 'activity' | 'recent' | 'past' = isEmpty
    ? 'activity'
    : isPastActivityScrolled || !hasRecentActivity
      ? 'past'
      : 'recent'

  const showMarkAllReadAction =
    !isEmpty && !isPastActivityScrolled && hasRecentActivity

  const topBarTitleNode =
    topBarTitle === 'activity' ? (
      <ScreenHeaderPersistentTopBarTitle textAnimationKey="activity">
        Activity feed
      </ScreenHeaderPersistentTopBarTitle>
    ) : topBarTitle === 'past' ? (
      <ScreenHeaderPersistentTopBarTitle textAnimationKey="past">
        Past activity
      </ScreenHeaderPersistentTopBarTitle>
    ) : (
      <ScreenHeaderPersistentTopBarTitle
        textAnimationKey="recent"
        action={
          showMarkAllReadAction && onMarkAsReadPress ? (
            <ScreenHeaderPersistentTopBarAction
              onPress={onMarkAsReadPress}
              isDisabled={isMarkAsReadPending}
              label="Mark all read"
            />
          ) : undefined
        }
      >
        Recent activity
      </ScreenHeaderPersistentTopBarTitle>
    )

  return (
    <Provider
      values={[
        [
          ActivityViewContext,
          {
            isPastActivityScrolled,
            setPastActivityScrolled,
            onMarkAsReadPress,
            isMarkAsReadPending,
          },
        ],
      ]}
    >
      <div className={cx(styles.root)} data-test={testId}>
        <ScreenHeaderProvider
          titleScrollProgressOffset={36}
          contentScrollProgressOffset={36}
          applyDocumentScrollPadding
          isOverlay
        >
          <ScreenHeaderPersistentTopBar fadeWhen="title-exit">
            <ScreenHeaderPersistentTopBarLayout
              right={
                !isSpacious && (
                  <IconButton
                    name="settings"
                    replace
                    to={links.activityPreferences()}
                    label="Open activity preferences"
                  />
                )
              }
            >
              {topBarTitleNode}
            </ScreenHeaderPersistentTopBarLayout>
          </ScreenHeaderPersistentTopBar>
          <ScreenHeaderMain>
            <ScreenHeaderContent>
              <header
                className={cx(isSpacious ? 'px-7xl' : 'px-3xl', 'pt-5xl')}
              >
                <div className="max-w-content mx-auto">
                  <ScreenHeaderTitle>
                    <div className="flex items-center pb-3xl">
                      <div className="flex-auto">
                        <h1 className="text-display-lg">Activity feed</h1>
                        <p className="text-body-lg mt-sm">
                          A collection of all your notifications
                        </p>
                      </div>
                      {isSpacious && (
                        <div>
                          <Button
                            replace
                            to={links.activityPreferences()}
                            variant="outline"
                            iconLeft="settings"
                            label="Settings"
                          />
                        </div>
                      )}
                    </div>
                  </ScreenHeaderTitle>
                </div>
              </header>
            </ScreenHeaderContent>
            <ScreenHeaderBorder />
          </ScreenHeaderMain>
        </ScreenHeaderProvider>
        <main className={isSpacious ? 'px-7xl' : 'px-3xl'}>{children}</main>
      </div>
    </Provider>
  )
}

interface ActivityViewContentProps {
  children: ReactNode
}

export function ActivityViewContent({ children }: ActivityViewContentProps) {
  return <div className="max-w-content mx-auto pb-2xl">{children}</div>
}

interface NotificationsSectionProps {
  title: string
  children: ReactNode
  isPastActivity?: boolean
}

export function NotificationsSection({
  title,
  children,
  isPastActivity,
}: NotificationsSectionProps) {
  const headerId = useId()
  const target = useRef<HTMLDivElement>(null)
  const prevScrollRef = useRef(0)
  const { setPastActivityScrolled, onMarkAsReadPress, isMarkAsReadPending } =
    useContext(ActivityViewContext)

  const { scrollYProgress } = useScroll({
    target,
    offset: ['-48px start', 'end end'],
  })
  useMotionValueEvent(
    scrollYProgress,
    'change',
    useCallback(
      (e) => {
        if (!isPastActivity) {
          return
        }

        if (prevScrollRef.current === 0 && e > 0) {
          setPastActivityScrolled(true)
        } else if (prevScrollRef.current !== 0 && e === 0) {
          setPastActivityScrolled(false)
        }
        prevScrollRef.current = e
      },
      [isPastActivity, setPastActivityScrolled],
    ),
  )
  return (
    <div className={styles.section} ref={target}>
      <div className={styles.sectionHeader}>
        <h2 id={headerId} className={styles.sectionTitle}>
          {title}
        </h2>
        {!isPastActivity && onMarkAsReadPress && (
          <div className={styles.sectionAction}>
            <Button
              isDisabled={isMarkAsReadPending}
              onPress={onMarkAsReadPress}
              iconLeft="tick"
              variant="text"
              label="Mark all read"
            />
          </div>
        )}
      </div>

      <GridList className={styles.items} aria-labelledby={headerId}>
        {children}
      </GridList>
    </div>
  )
}

export function NotificationsSectionSkeleton({
  title,
  children,
  isPastActivity,
}: NotificationsSectionProps) {
  return (
    <IsSkeletonContext value={true}>
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>
            <AutoSkeleton>{title}</AutoSkeleton>
          </h2>
          {!isPastActivity && (
            <div className={styles.sectionAction}>
              <Button variant="text" iconLeft="tick" label="Placeholder" />
            </div>
          )}
        </div>

        {children}
      </div>
    </IsSkeletonContext>
  )
}

export function NotificationsEmptyState() {
  return (
    <EmptyState size="lg">
      <EmptyStateIconDecoration icon="notifications" />
      <EmptyStateTitle>No notifications right now</EmptyStateTitle>
      <EmptyStateDescription>
        New notifications will appear here whenever there's activity.
      </EmptyStateDescription>
    </EmptyState>
  )
}

const minidownClassNames: MinidownClassNames = {
  p: 'text-body-md',
  strong: 'text-headline-md',
}

interface NotificationItemProps {
  textValue: string
  to?: string
  onAction?: () => void
  text: string | MinidownNode
  dispatchedAt: Date
  avatar: ReactNode
}

export function NotificationItem({
  textValue,
  to,
  onAction,
  text,
  dispatchedAt,
  avatar,
}: NotificationItemProps) {
  return (
    <GridListItem
      textValue={textValue}
      className={({ isFocusVisible }) =>
        cx(styles.notificationItem, isFocusVisible && styles.focusVisible)
      }
      href={to}
      onAction={onAction}
    >
      <div className={styles.imageContainer}>{avatar}</div>
      <div className={styles.content}>
        <MinidownContent classNames={minidownClassNames} value={text} />
        <time
          className={styles.notificationTimestamp}
          dateTime={dispatchedAt.toString()}
        >
          {dateFormatters.dateTimeDefault.format(dispatchedAt)}
        </time>
      </div>
      {!!to && (
        <div className={styles.icon}>
          <Icon variant="secondary" name="chevron" />
        </div>
      )}
    </GridListItem>
  )
}

export function NotificationItemSkeleton() {
  const { ref, varyPercent } = useSkeletonListContext()
  return (
    <div
      className={cx(styles.notificationItem, styles.skeleton)}
      ref={composeRefs(ref)}
    >
      <div className={styles.imageContainer}>
        <Avatar size={48} subject={{}} isSkeleton />
      </div>
      <div className={styles.content}>
        <p className="text-body-md">
          <Skeleton width={varyPercent(70)} />
        </p>
        <p className={styles.notificationTimestamp}>
          <Skeleton width={varyPercent(50)} />
        </p>
      </div>
      <div className={styles.icon}>
        <Icon skeleton="full" variant="secondary" name="chevron" />
      </div>
    </div>
  )
}
